/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { VoiceBubble, VoiceRecorder } from "../../components/VoiceMessage";
import { toast } from "sonner";
import { Download, Search, Loader2, MessageSquareText, Reply } from "lucide-react";

export default function TranscriptionsPage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [f, setF] = useState({ patient_id: "", date_from: "", date_to: "", q: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/patients/mine").then(({ data }) => setPatients(data)).catch(() => null); }, []);

  const qs = () => Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/therapist/transcriptions?${qs()}`); setData(data); }
    catch { toast.error("Chargement impossible"); }
    setBusy(false);
  };
  useEffect(() => { load(); }, [f.patient_id, f.date_from, f.date_to]); // eslint-disable-line

  const exportCsv = () => {
    const a = document.createElement("a");
    a.href = `${API}/therapist/transcriptions.csv?${qs()}${qs() ? "&" : ""}auth=${encodeURIComponent(localStorage.getItem("bc_token") || "")}`;
    a.download = "bombacare-transcriptions.csv"; a.click();
  };

  const inputCls = "h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] bg-white focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]";
  return (
    <div className="bc-in flex flex-col gap-5" data-testid="transcriptions-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-[Fraunces] text-2xl md:text-3xl font-bold text-[#0A1426]">Transcriptions vocales</h1>
          <div className="text-[12px] text-[#0A1426]/60">Messages vocaux de vos patients (et vos réponses), transcrits automatiquement par Whisper.</div>
        </div>
        <Button data-testid="transcriptions-export-csv" onClick={exportCsv} variant="outline" className="h-11 rounded-full border-[#0A1426] text-[#0A1426] font-bold"><Download size={16} className="mr-2" /> Export CSV</Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E4E0D5] p-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="transcriptions-filters">
        <select data-testid="filter-patient" value={f.patient_id} onChange={(e) => setF({ ...f, patient_id: e.target.value })} className={inputCls}>
          <option value="">Tous les patients</option>
          {patients.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
        </select>
        <input data-testid="filter-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={inputCls} />
        <input data-testid="filter-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={inputCls} />
        <div className="flex gap-1">
          <input data-testid="filter-q" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Rechercher un mot…" className={`${inputCls} flex-1 min-w-0`} />
          <button data-testid="filter-search-btn" onClick={load} className="w-11 h-11 rounded-full bg-[#2BA3A0] text-white grid place-items-center shrink-0"><Search size={16} /></button>
        </div>
      </div>

      {busy && !data ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div> : data && (
        <>
          <div data-testid="transcriptions-count" className="text-[12px] text-[#0A1426]/70">{data.total} message(s) vocal(aux) · {data.transcrits} transcrit(s)</div>
          {data.items.length === 0 && <div className="bg-white rounded-2xl border border-[#E4E0D5] p-8 text-center text-[13px] text-[#0A1426]/60">Aucun message vocal pour ces critères.</div>}
          <div className="flex flex-col gap-2">
            {data.items.map((m) => (
              <div key={m.id} data-testid="transcription-row" className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex flex-col md:flex-row md:items-start gap-3">
                <div className="md:w-56 shrink-0">
                  <button data-testid="transcription-patient-link" onClick={() => m.patient_id && navigate(`/pro/patients/${m.patient_id}`)} className="font-bold text-[#0A1426] text-left hover:underline">{m.patient_nom}</button>
                  <div className="text-[11px] text-[#0A1426]/60">{new Date(m.date).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</div>
                  <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full font-bold ${m.auteur === "patient" ? "bg-[#F5F3EE] text-[#0A1426]" : "bg-[#2BA3A0]/15 text-[#2BA3A0]"}`}>{m.auteur === "patient" ? "Patient" : "Vous"}</span>
                </div>
                <div className="flex-1 min-w-0">
                  {m.transcription
                    ? <p data-testid="transcription-text" className="text-[14px] text-[#0A1426] leading-snug flex items-start gap-2"><MessageSquareText size={16} className="text-[#2BA3A0] shrink-0 mt-0.5" />« {m.transcription} »</p>
                    : <p className="text-[12px] text-[#0A1426]/60 italic">{m.transcription_statut === "en_cours" ? "Transcription en cours…" : "Pas de transcription disponible"}</p>}
                  {m.auteur === "patient" && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="transcription-reply">
                      {m.reponse ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] text-[#2BA3A0] font-bold flex items-center gap-1"><Reply size={12} /> Votre réponse vocale</span>
                          <VoiceBubble msg={m.reponse} mine onRefresh={load} />
                        </div>
                      ) : (
                        <>
                          <VoiceRecorder threadId={m.thread_id} destinataireId={m.expediteur_id} size="sm" onSent={() => { toast.success(`Réponse envoyée à ${m.patient_nom}`); load(); }} />
                          <span className="text-[11px] text-[#0A1426]/60">Répondre par un vocal (transcrit et notifié au patient)</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="shrink-0"><VoiceBubble msg={{ ...m, transcription: null }} mine={m.auteur !== "patient"} onRefresh={load} /></div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
